/**
 * What the dashboard is built from: the wire shape of
 * `GET /api/v1/dashboard/`, and the summary the screen renders once
 * `dashboardService` has mapped it.
 *
 * The recent clients are clients, so they are carried as the `Client` row the
 * My clients table already uses rather than as a shape of their own - the two
 * tables show the same five columns of the same records, and the endpoint now
 * sends the same engagement duration and status the clients list does.
 */

import type { Client } from "../../clients/types/clients.types";

/** The months both charts label their marks with, in the order they are sent. */
export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/* ── Wire shapes ───────────────────────────────────────────────────────────
 * snake_case exactly as the backend sends it. Only `dashboardService` should
 * touch these - it maps them to the app's own types, so the API's casing stops
 * at the service boundary.
 */

/** A `{ id, name }` reference, as the goal is embedded in a recent client. */
export interface DashboardLookupResponse {
  id: number;
  name: string;
}

/**
 * One of the newest clients. A trimmed client record - enough for a row of the
 * table and the id to open one by, and nothing else.
 *
 * The last two fields are the same ones `GET /api/v1/clients/` carries, under
 * the same names and the same constants, which is what lets one row shape serve
 * both tables: how long the plan they are on runs for, and where it stands.
 */
export interface RecentClientResponse {
  public_id: string;
  first_name: string;
  last_name: string;
  /**
   * Every goal the client is signed up against. Loosely typed on purpose: the
   * field used to be a single `goal`, and a backend part way through that
   * change sends one unwrapped object here — see `toLookupReferences`.
   */
  goals?: DashboardLookupResponse | DashboardLookupResponse[] | null;
  /** What the field was called before it took several. Still read. */
  goal?: DashboardLookupResponse | null;
  created_at: string;
  /** `1 Month`, and `null` for a client with no plan running. */
  current_engagement_duration: string | null;
  /** `ACTIVE`, `ENDING_SOON`, `INACTIVE`, `NEW`. */
  status: string | null;
}

/** One point of the New clients chart: `{ month: "Aug", count: 3 }`. */
export interface MonthlyClientsResponse {
  /** The three-letter month name, already written for the axis: `Jan`, `Feb`. */
  month: string;
  count: number;
}

/**
 * One month of the earnings chart: `{ month: "Aug", amount: "28200.00" }`.
 *
 * The amount is a decimal string, the way the backend keeps money everywhere
 * else - see `toMoney`, which is what turns it into the number a bar is drawn
 * from.
 */
export interface MonthlyIncomeResponse {
  month: string;
  amount: string;
}

/**
 * The whole payload.
 *
 * Every field is nullable because the backend sends `null` for the figures it
 * cannot count yet. That is "no figure", not zero, and the mapping keeps the
 * two apart so the screen can show a dash rather than a number nobody has
 * measured.
 */
export interface DashboardResponse {
  /** Everyone on the books, archived clients included. */
  total_clients: number | null;
  /** The subset with a plan running - what the headline tile counts. */
  active_clients: number | null;
  /** A percentage as a number: `25.0` means a quarter of requests converted. */
  client_conversion_rate: number | null;
  recent_clients: RecentClientResponse[] | null;
  /** Twelve entries, Jan to Dec of the current year, zeros included. */
  new_clients_by_month: MonthlyClientsResponse[] | null;
  plans_created: number | null;
  plans_created_in_7_days: number | null;
  /** Every payment request ever raised - the pie's whole. */
  total_requests: number | null;
  new_requests_in_7_days: number | null;
  /** Plans due to run out inside the week, which is who needs chasing. */
  renewals_in_7_days: number | null;
  /** Rupees taken in all year, as a decimal string: `"28200.00"`. */
  total_income: string | null;
  /** Twelve entries, Jan to Dec of the current year, zeros included. */
  income_by_month: MonthlyIncomeResponse[] | null;
}

/* ── App shapes ─────────────────────────────────────────────────────────── */

/** One point of the New clients chart, in the app's own casing. */
export interface MonthlyClients {
  month: string;
  clients: number;
}

/** One bar of the earnings chart, in rupees. */
export interface MonthlyIncome {
  month: string;
  amount: number;
  /**
   * Where the month sits against today, which is what the bar is coloured by:
   * the year's remaining months have nothing in them yet, and a row of zero
   * bars in the brand colour reads as months that took nothing rather than
   * months that have not happened. `future` draws the flat "no data" stub.
   */
  status: "past" | "current" | "future";
}

/**
 * The dashboard, as the screen holds it.
 *
 * A `null` count means the backend has no figure for it - either because it
 * does not count that yet, or because the summary has not arrived. Both read
 * the same way on screen, which is a dash rather than a zero: a "0" beside a
 * label reads as an answer.
 */
export interface DashboardSummary {
  /** Everyone on the books - what says whether the workspace has started. */
  totalClients: number | null;
  /** Those with a plan running, which is the "Active clients" tile. */
  activeClients: number | null;
  /** The share of requests that turned into clients, as a percentage. */
  conversionRate: number | null;
  plansCreated: number | null;
  plansCreatedInLast7Days: number | null;
  totalRequests: number | null;
  newRequestsInLast7Days: number | null;
  /** Plans running out inside the coming week. */
  renewalsInNext7Days: number | null;
  /** Rupees taken in all year. */
  totalIncome: number | null;
  /** Empty until the summary lands; the chart plots nothing rather than mock. */
  incomeByMonth: MonthlyIncome[];
  /** Empty until the summary lands; the chart plots nothing rather than mock. */
  newClientsByMonth: MonthlyClients[];
  /** Newest first, as the backend orders them. */
  recentClients: Client[];
}

/** What the screen shows before anything has arrived, and after a failed load. */
export const EMPTY_DASHBOARD: DashboardSummary = {
  totalClients: null,
  activeClients: null,
  conversionRate: null,
  plansCreated: null,
  plansCreatedInLast7Days: null,
  totalRequests: null,
  newRequestsInLast7Days: null,
  renewalsInNext7Days: null,
  totalIncome: null,
  incomeByMonth: [],
  newClientsByMonth: [],
  recentClients: [],
};
